import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateJobFolderInput, JobFolder, RenameJobFolderInput } from '@jobos/shared';

import { apiClient } from '@/api/client';
import { useToast } from '@/hooks/use-toast';
import { jobFileKeys } from '@/hooks/useJobFiles';

// The folder half of the job file store's data layer. Follows useJobFiles
// exactly: a key factory, React Query hooks, toasts on error.
//
// Every mutation invalidates the *file* lists as well as the folder list. A
// folder change moves files — creating one gives them somewhere to go, renaming
// one changes what the breadcrumb says, deleting one returns its files to the
// job root — so a folder-only invalidation would leave the grid stale.

export const jobFolderKeys = {
  all: ['jobFolders'] as const,
  list: (jobId: string) => [...jobFolderKeys.all, 'list', jobId] as const,
};

export function useJobFolders(jobId: string | undefined) {
  return useQuery({
    queryKey: jobFolderKeys.list(jobId!),
    queryFn: async () => {
      const res = await apiClient.get<{ folders: JobFolder[] }>(`/jobs/${jobId}/folders`);
      return res.folders;
    },
    enabled: !!jobId,
  });
}

/** Both lists a folder change can invalidate. */
function useFolderInvalidation(jobId: string) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: jobFolderKeys.list(jobId) });
    queryClient.invalidateQueries({ queryKey: jobFileKeys.lists(jobId) });
  };
}

function useFolderErrorToast(fallback: string) {
  const { toast } = useToast();
  return (error: Error) => {
    toast({
      title: 'Error',
      description: error.message || fallback,
      variant: 'destructive',
    });
  };
}

export function useCreateJobFolder(jobId: string) {
  const invalidate = useFolderInvalidation(jobId);
  const onError = useFolderErrorToast('Failed to create the folder.');

  return useMutation({
    mutationFn: async (input: CreateJobFolderInput) => {
      const res = await apiClient.post<{ folder: JobFolder }>(`/jobs/${jobId}/folders`, input);
      return res.folder;
    },
    onSuccess: invalidate,
    onError,
  });
}

export function useRenameJobFolder(jobId: string) {
  const invalidate = useFolderInvalidation(jobId);
  const onError = useFolderErrorToast('Failed to rename the folder.');

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: RenameJobFolderInput }) => {
      const res = await apiClient.patch<{ folder: JobFolder }>(
        `/jobs/${jobId}/folders/${id}`,
        input,
      );
      return res.folder;
    },
    onSuccess: invalidate,
    onError,
  });
}

export function useDeleteJobFolder(jobId: string) {
  const invalidate = useFolderInvalidation(jobId);
  const onError = useFolderErrorToast('Failed to delete the folder.');

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/jobs/${jobId}/folders/${id}`);
    },
    onSuccess: invalidate,
    onError,
  });
}
